/**
 * The menu bar's contents, as data.
 *
 * This was markup once, with a guard on every single item — 300 lines in
 * which the roles that gate a link sat several levels away from the link
 * itself. Written down here, a section and its items can be read against the
 * route table they point at.
 *
 * `roles` means any of them, which is what every route guard uses. An item
 * with no roles is open to anyone signed in.
 */

import { PRODUCTION_DASHBOARD_ROLES, PROJECTS_DASHBOARD_ROLES } from '../shared/dashboard-roles';

export interface MenuItem {
  label: string;
  /** An in-app route, or an absolute URL for the two that leave the product. */
  to: string;
  roles?: string[];
  /** Hidden when false — for items behind a feature flag or a setting. */
  when?: (context: MenuContext) => boolean;
  /**
   * Draws a rule above this item, so a long section reads as groups rather
   * than a list. Ignored when the item comes out first — the items above it
   * can all be gated away, and a rule along the top of a dropdown is noise.
   */
  startsGroup?: boolean;
}

export interface MenuSection {
  label: string;
  roles?: string[];
  items: MenuItem[];
}

/** What the menu needs to know beyond the signed-in user's roles. */
export interface MenuContext {
  /** The new quote document work, which brings three quoting screens with it. */
  newQuoteDocumentEnabled: boolean;
}

const INTERNAL = ['internal-user', 'auditor'];

export const MENU: MenuSection[] = [
  {
    label: 'Sales',
    roles: INTERNAL,
    items: [
      { label: 'Dashboard', to: '/sales/dashboard', roles: ['admin'] },
      { label: 'Enquiries', to: '/sales/enquiries/view', roles: INTERNAL, startsGroup: true },
      { label: 'Enquiry Board', to: '/sales/enquiries/board', roles: INTERNAL },
      { label: 'Distributors', to: '/sales/distributors', roles: INTERNAL },
      {
        label: 'Enquiries Reports',
        to: '/sales/enquiries/reports',
        roles: INTERNAL,
        startsGroup: true,
      },
      { label: 'Sales Reports', to: '/sales/reports', roles: INTERNAL },
      { label: 'Budget Quotes', to: '/sales/budget-quotes', roles: INTERNAL },
    ],
  },
  {
    label: 'Quoting',
    roles: INTERNAL,
    items: [
      { label: 'Dashboard', to: '/quoting/dashboard', roles: ['admin'] },
      { label: 'Quotes', to: '/quoting/quotes', roles: INTERNAL, startsGroup: true },
      {
        label: 'To Send',
        to: '/quoting/quote-send-queue',
        roles: ['admin', 'sales-admin'],
        when: (context) => context.newQuoteDocumentEnabled,
      },
      {
        label: 'Follow-ups',
        to: '/quoting/quote-follow-ups',
        roles: INTERNAL,
        when: (context) => context.newQuoteDocumentEnabled,
      },
      {
        label: 'Conversion',
        to: '/quoting/quote-conversion',
        roles: INTERNAL,
        when: (context) => context.newQuoteDocumentEnabled,
      },
    ],
  },
  {
    // Named for what it holds. The planner, the installers and the drawing
    // bookings are still on /contracts/* routes; the screens people actually
    // open all day are projects, so the section says so.
    label: 'Projects',
    roles: INTERNAL,
    items: [
      { label: 'Dashboard', to: '/projects/dashboard', roles: PROJECTS_DASHBOARD_ROLES },
      { label: 'Projects', to: '/projects', roles: INTERNAL, startsGroup: true },
      { label: 'Board', to: '/projects/board', roles: INTERNAL },
      { label: 'My Tasks', to: '/projects/my-tasks', roles: INTERNAL },
      // Qualified, because Production has a planner too.
      { label: 'Contracts Planner', to: '/contracts/planner', roles: INTERNAL },
      { label: 'Drawing Bookings', to: '/contracts/drawings', roles: INTERNAL },
      // Reading installers is business-wide; the write controls on the screen
      // are what installer-certificates-admin and admin own. This list used to
      // name those two, which put a link in front of the one role the route
      // guard turned away.
      { label: 'Installers', to: '/contracts/installers', roles: INTERNAL },
      {
        label: 'Project Savings',
        to: '/reports/project-savings',
        roles: INTERNAL,
        startsGroup: true,
      },
      { label: 'Reports', to: '/contracts/reports', roles: INTERNAL },
    ],
  },
  {
    label: 'Production',
    roles: INTERNAL,
    items: [
      { label: 'Dashboard', to: '/production/dashboard', roles: PRODUCTION_DASHBOARD_ROLES },
      // In the order work moves through the factory.
      {
        label: 'Planner',
        to: '/production/planner',
        roles: ['auditor', 'internal-user'],
        startsGroup: true,
      },
      { label: 'Detailing', to: '/production/detailing', roles: INTERNAL },
      {
        label: 'Detailers Diary',
        to: '/production/detailers-diary',
        roles: ['internal-user', 'auditor', 'admin', 'production-admin'],
      },
      { label: 'Orders', to: '/production/orders', roles: INTERNAL },
      {
        label: 'Stock Control',
        to: '/production/stock-control',
        roles: ['admin', 'production-admin', 'auditor'],
      },
      { label: 'Suppliers', to: '/production/suppliers', roles: INTERNAL },
      {
        // /production/report, not /production/reports — a single report, and
        // the one it names. It sits with the reports rather than beside the
        // planner; the label is the page's own heading.
        label: 'Date Changes',
        to: '/production/report',
        roles: ['admin', 'production-admin', 'auditor'],
        startsGroup: true,
      },
      { label: 'Reports', to: '/production/reports', roles: INTERNAL },
    ],
  },
  {
    label: 'Accounting',
    roles: ['admin', 'accounts-manager', 'administrative-assistant'],
    items: [
      {
        label: 'Dashboard',
        to: '/accounting/dashboard',
        roles: ['admin'],
      },
      {
        label: 'Order Acknowledgements',
        to: '/accounting/order-acknowledgements',
        roles: ['admin', 'accounts-manager'],
        startsGroup: true,
      },
      {
        label: 'Search Acknowledgements',
        to: '/accounting/order-acknowledgements/search',
        roles: ['admin', 'accounts-manager'],
      },
      {
        label: 'Purchase Orders',
        to: '/accounting/purchase-orders',
        roles: ['admin', 'accounts-manager', 'administrative-assistant'],
      },
      {
        label: 'Reports',
        to: '/accounting/reports',
        roles: ['admin', 'accounts-manager', 'administrative-assistant'],
      },
    ],
  },
  {
    // After the departments rather than above them: the five sections before
    // this one are each a department's view of its own work, and this reads
    // across all of them, so it follows the things it is a reading of.
    //
    // One item, which the rest of this menu would normally consolidate away —
    // Tools exists because two single-item dropdowns stood here once. It keeps
    // its own section anyway: there is no department it belongs under, and
    // filing the company's numbers inside any one of them would say something
    // untrue about who they are for.
    label: 'Directors',
    roles: ['admin'],
    items: [{ label: 'Dashboard', to: '/directors/dashboard', roles: ['admin'] }],
  },
  {
    // What belongs to no one domain. Two single-item dropdowns used to stand
    // here instead.
    label: 'Tools',
    roles: INTERNAL,
    items: [
      { label: 'GP Calculator', to: '/utils/gp-calculator', roles: INTERNAL },
      {
        label: 'Superset Reports',
        to: 'https://superset.iqd-pro.com/dashboard/list/',
        roles: INTERNAL,
      },
    ],
  },
  {
    // Last, and grouped: the overview, then the catalogue and its prices, the
    // documents a quote is built from, who may sign in and what we talk to,
    // then the logs the overview summarises.
    label: 'Admin',
    roles: INTERNAL,
    items: [
      { label: 'Dashboard', to: '/admin/dashboard', roles: ['admin'] },
      { label: 'Data Management', to: '/data-management', roles: INTERNAL, startsGroup: true },
      { label: 'Scheduled Prices', to: '/data-management/scheduled-prices', roles: INTERNAL },
      {
        label: 'Quote Templates',
        to: '/data-management/quote-templates',
        roles: ['admin'],
        startsGroup: true,
      },
      { label: 'Email Templates', to: '/data-management/email-templates', roles: ['admin'] },
      {
        label: 'Quotation Collateral',
        to: '/data-management/portal-collateral',
        roles: ['admin'],
      },
      { label: 'Letterhead', to: '/data-management/letterhead', roles: ['admin'] },
      {
        label: 'Users',
        to: '/user-management',
        roles: ['admin', 'sales-admin'],
        startsGroup: true,
      },
      { label: 'Integrations', to: '/integrations', roles: ['admin', 'sales-admin'] },
      {
        label: 'Email Log',
        to: '/data-management/email-logs',
        roles: ['admin', 'auditor'],
        startsGroup: true,
      },
      { label: 'Audit Log', to: '/data-management/audit-log', roles: ['admin', 'auditor'] },
      { label: 'Who’s Online', to: '/data-management/online-users', roles: ['admin', 'auditor'] },
    ],
  },
];

export function isExternal(to: string): boolean {
  return to.startsWith('http://') || to.startsWith('https://');
}

/** True when `pathname` is `to` or a route underneath it. */
function covers(to: string, pathname: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * The item whose route this path is on, and the section holding it.
 *
 * The longest match wins, because the shorter ones are real routes too:
 * `/projects` is the Projects item and `/projects/board` is Board, and a path
 * under the second is under the first by prefix as well. Matching only at a
 * segment boundary keeps `/quoting/quotes` off `/quoting/quotes-anything`.
 *
 * This exists for the mobile menu, which is shut most of the time and so has
 * nowhere to put the `NavLink` highlight the desktop bar relies on: it names
 * the open section on the button, and opens that section when the drawer is.
 */
export function activeMenuPath(
  sections: MenuSection[],
  pathname: string,
): { section: MenuSection; item: MenuItem } | undefined {
  let best: { section: MenuSection; item: MenuItem } | undefined;

  for (const section of sections) {
    for (const item of section.items) {
      if (isExternal(item.to) || !covers(item.to, pathname)) continue;
      if (!best || item.to.length > best.item.to.length) best = { section, item };
    }
  }

  return best;
}

function allowed(roles: string[] | undefined, userRoles: string[]): boolean {
  if (!roles || roles.length === 0) return true;
  return roles.some((role) => userRoles.includes(role));
}

/**
 * The sections and items this user may see. A section whose items are all
 * gated away is dropped rather than left as an empty dropdown.
 */
export function visibleMenu(userRoles: string[], context: MenuContext): MenuSection[] {
  return MENU.filter((section) => allowed(section.roles, userRoles))
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => allowed(item.roles, userRoles) && (item.when ? item.when(context) : true),
      ),
    }))
    .filter((section) => section.items.length > 0);
}
